/**
 * ProjectionsControlPanel Component
 *
 * Control panel for the Projections page with location selector,
 * status badge, and action buttons (Edit/Save).
 */

import React, { useState } from 'react';
import {
  PencilIcon,
  CheckIcon,
  XMarkIcon,
  ArrowPathIcon,
  LockOpenIcon,
  ExclamationTriangleIcon,
  ArchiveBoxIcon,
} from '@heroicons/react/24/outline';
import { LocationDropdown } from '../LocationDropdown';
import { ProjectionStatusBadge } from './ProjectionStatusBadge';
import { ProjectionStatus, ProjectionMetadata, EditingWindowStatus } from '../../types/projections';
import { requestEditOverride, requestArchiveUnlock } from '../../services/projectionsApi';

interface ProjectionsControlPanelProps {
  locationId: number;
  targetYear: number;
  status: ProjectionStatus;
  metadata: ProjectionMetadata | null;
  onLocationChange: (locationId: number) => void;
  // Year selector for archive
  availableYears: number[];
  currentTargetYear: number;
  onYearChange: (year: number) => void;
  // Edit state
  isEditing: boolean;
  hasUnsavedChanges: boolean;
  canEdit: boolean;
  // Actions
  onStartEdit: () => void;
  onSave: () => void;
  onCancel: () => void;
  onRefresh: () => void;
  // Permissions
  isExecutive: boolean;
  // Loading states
  saving: boolean;
  loading: boolean;
  // New: editing window status
  editingWindowStatus?: EditingWindowStatus;
  isCurrentTargetYear: boolean;
  // Archive unlock state (managed by parent)
  isArchiveUnlocked: boolean;
  onArchiveUnlocked: () => void;
  // Consolidated view (read-only, no editing)
  isConsolidated?: boolean;
}

export const ProjectionsControlPanel: React.FC<ProjectionsControlPanelProps> = ({
  locationId,
  targetYear,
  status,
  metadata,
  onLocationChange,
  availableYears,
  currentTargetYear,
  onYearChange,
  isEditing,
  hasUnsavedChanges,
  canEdit,
  onStartEdit,
  onSave,
  onCancel,
  onRefresh,
  isExecutive,
  saving,
  loading,
  editingWindowStatus,
  isCurrentTargetYear,
  isArchiveUnlocked,
  onArchiveUnlocked,
  isConsolidated = false,
}) => {
  // isExecutive = canUnlockProjections (Admin/CEO/Executive): may grant the current-year
  // 24h override AND unlock archived years

  const [unlocking, setUnlocking] = useState(false);
  const [unlockError, setUnlockError] = useState<string | null>(null);

  // Archive unlock modal state
  const [showArchiveUnlockModal, setShowArchiveUnlockModal] = useState(false);
  const [archiveUnlockReason, setArchiveUnlockReason] = useState('');
  const [archiveUnlocking, setArchiveUnlocking] = useState(false);
  const [archiveUnlockError, setArchiveUnlockError] = useState<string | null>(null);

  const isDisabled = loading || saving || unlocking;
  const isFinal = status === 'final';

  // Show unlock button for executives when:
  // 1. It's the current target year
  // 2. Status is final (outside editing window)
  // 3. No active override
  // 4. User is executive
  // 5. NOT consolidated view (consolidated is always read-only)
  const showUnlockButton = !isConsolidated &&
    isCurrentTargetYear &&
    isFinal &&
    !editingWindowStatus?.hasExecutiveOverride &&
    isExecutive;

  // Handle executive unlock
  const handleUnlock = async () => {
    setUnlocking(true);
    setUnlockError(null);

    try {
      const response = await requestEditOverride({
        locationId,
        targetYear,
      });

      if (response.success) {
        // Refresh to get updated canEdit status
        onRefresh();
      } else {
        setUnlockError(response.error || 'Failed to unlock projection');
      }
    } catch (err: any) {
      setUnlockError(err.message || 'Failed to unlock projection');
    } finally {
      setUnlocking(false);
    }
  };

  // Handle archive unlock (for past years - Admin only)
  const handleArchiveUnlock = async () => {
    if (!archiveUnlockReason.trim()) {
      setArchiveUnlockError('Please provide a reason for unlocking this archived projection');
      return;
    }

    setArchiveUnlocking(true);
    setArchiveUnlockError(null);

    try {
      // Call the archive unlock API (authenticated client - the previous raw fetch read an
      // idToken from sessionStorage that is never set, so it always went out unauthenticated)
      const data = await requestArchiveUnlock({
        locationId,
        targetYear,
        reason: archiveUnlockReason,
      });

      if (data.success) {
        // Notify parent that archive is unlocked
        onArchiveUnlocked();
        setShowArchiveUnlockModal(false);
        setArchiveUnlockReason('');
        // Refresh to get updated data
        onRefresh();
      } else {
        setArchiveUnlockError(data.error || 'Failed to unlock archived projection');
      }
    } catch (err: any) {
      setArchiveUnlockError(err.message || 'Failed to unlock archived projection');
    } finally {
      setArchiveUnlocking(false);
    }
  };

  // Show archive unlock button for Admin/CEO/Executive viewing past years (when not already unlocked)
  // Not shown for consolidated view (consolidated is always read-only)
  const showArchiveUnlockButton = !isConsolidated && !isCurrentTargetYear && isExecutive && !isArchiveUnlocked;

  return (
    <>
      {/* Archive Unlock Modal */}
      {showArchiveUnlockModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowArchiveUnlockModal(false)} />
            <div className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
              <div className="sm:flex sm:items-start">
                <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-amber-100 sm:mx-0 sm:h-10 sm:w-10">
                  <ExclamationTriangleIcon className="h-6 w-6 text-amber-600" aria-hidden="true" />
                </div>
                <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left flex-1">
                  <h3 className="text-base font-semibold leading-6 text-gray-900">
                    Unlock Archived Projection
                  </h3>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500">
                      You are about to unlock the <strong>{targetYear}</strong> archived projection for editing.
                      This action will be logged in the audit trail.
                    </p>
                    <div className="mt-4">
                      <label htmlFor="unlock-reason" className="block text-sm font-medium text-gray-700">
                        Reason for unlocking <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        id="unlock-reason"
                        rows={3}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-amber-500 focus:ring-amber-500 sm:text-sm"
                        placeholder="e.g., Correcting data entry error in Service department expenses"
                        value={archiveUnlockReason}
                        onChange={(e) => setArchiveUnlockReason(e.target.value)}
                      />
                    </div>
                    {archiveUnlockError && (
                      <p className="mt-2 text-sm text-red-600">{archiveUnlockError}</p>
                    )}
                  </div>
                </div>
              </div>
              <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  disabled={archiveUnlocking}
                  onClick={handleArchiveUnlock}
                  className="inline-flex w-full justify-center rounded-md bg-amber-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-amber-500 sm:ml-3 sm:w-auto disabled:opacity-50"
                >
                  {archiveUnlocking ? 'Unlocking...' : 'Unlock for Editing'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowArchiveUnlockModal(false);
                    setArchiveUnlockReason('');
                    setArchiveUnlockError(null);
                  }}
                  className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow mb-6 overflow-hidden">
        {/* Archive Warning Banner - shown when viewing past years (not for consolidated) */}
        {!isConsolidated && !isCurrentTargetYear && (
          <div className={`px-4 py-3 flex items-center gap-3 ${isArchiveUnlocked ? 'bg-amber-50 border-b border-amber-200' : 'bg-yellow-50 border-b border-yellow-200'}`}>
            <ArchiveBoxIcon className={`h-5 w-5 flex-shrink-0 ${isArchiveUnlocked ? 'text-amber-600' : 'text-yellow-600'}`} />
            <div className="flex-1">
              <p className={`text-sm font-medium ${isArchiveUnlocked ? 'text-amber-800' : 'text-yellow-800'}`}>
                {isArchiveUnlocked
                  ? `Archived Projection Unlocked - You are editing the ${targetYear} projection. Changes will be logged.`
                  : `Viewing ${targetYear} Archived Projection (Read-Only)`
                }
              </p>
              <p className={`text-xs mt-0.5 ${isArchiveUnlocked ? 'text-amber-600' : 'text-yellow-600'}`}>
                {isArchiveUnlocked
                  ? 'Remember to save your changes before navigating away.'
                  : 'This projection has been finalized. Historical projections are preserved for comparison with actual results.'
                }
              </p>
            </div>
            {showArchiveUnlockButton && (
              <button
                onClick={() => setShowArchiveUnlockModal(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-amber-700 bg-amber-100 rounded hover:bg-amber-200 transition-colors text-sm font-medium whitespace-nowrap"
              >
                <LockOpenIcon className="h-4 w-4" />
                Unlock for Corrections
              </button>
            )}
          </div>
        )}

        {/* Dark Header */}
        <div className="px-4 py-3 bg-gray-800 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h2 className="text-base font-semibold text-white">{targetYear} Projections</h2>
          {/* Year Selector for Archive */}
          {availableYears.length > 1 && (
            <select
              value={targetYear}
              onChange={(e) => onYearChange(parseInt(e.target.value))}
              disabled={isEditing || isDisabled}
              className="bg-gray-700 text-white text-sm font-medium rounded-md border-gray-600 px-2 py-1 focus:border-blue-500 focus:ring-blue-500 cursor-pointer hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {availableYears.map((year) => (
                <option key={year} value={year}>
                  {year}{year === currentTargetYear ? ' (Current)' : ' (Archive)'}
                </option>
              ))}
            </select>
          )}
          {/* Consolidated Badge */}
          {isConsolidated && (
            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-600 text-blue-100">
              Consolidated (Read-Only)
            </span>
          )}
          {/* Archive Badge */}
          {!isConsolidated && !isCurrentTargetYear && (
            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
              isArchiveUnlocked
                ? 'bg-amber-600 text-amber-100'
                : 'bg-gray-600 text-gray-200'
            }`}>
              {isArchiveUnlocked ? 'Archive Unlocked' : 'Read-Only'}
            </span>
          )}
        </div>
        {/* Status Badge */}
        <ProjectionStatusBadge
          status={status}
          editingWindowStatus={editingWindowStatus}
          lastEditedBy={metadata?.lastEditedByName}
          lastEditedAt={metadata?.lastEditedAt}
        />
      </div>

      {/* Controls Row */}
      <div className="px-4 py-3 flex flex-wrap items-center gap-3 bg-gray-50 border-b border-gray-200">
        {/* Location Selector */}
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-gray-600 whitespace-nowrap">Location:</label>
          <LocationDropdown
            value={locationId}
            onChange={onLocationChange}
            disabled={isDisabled || isEditing}
            className="text-sm py-1.5"
          />
        </div>

        {/* Target Year Display */}
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-gray-600">Year:</label>
          <span className="px-3 py-1.5 bg-blue-100 text-blue-800 rounded-md font-semibold text-sm">
            {targetYear}
          </span>
        </div>

        {/* Last Edited Info */}
        {metadata?.lastEditedByName && (
          <span className="text-xs text-gray-500">
            Last edited by {metadata.lastEditedByName}
            {metadata.lastEditedAt && ` on ${new Date(metadata.lastEditedAt).toLocaleString()}`}
          </span>
        )}

        {/* Override Info */}
        {editingWindowStatus?.hasExecutiveOverride && (
          <span className="text-xs text-amber-600 font-medium">
            Override active until {editingWindowStatus.overrideExpiresAt
              ? new Date(editingWindowStatus.overrideExpiresAt).toLocaleString()
              : 'unknown'}
          </span>
        )}

        {/* Unlock Error */}
        {unlockError && (
          <span className="text-xs text-red-600">{unlockError}</span>
        )}

        {/* Spacer */}
        <div className="ml-auto flex items-center gap-2">
          {/* Refresh Button */}
          <button
            onClick={onRefresh}
            disabled={isDisabled || isEditing}
            className="flex items-center gap-1.5 px-3 py-1.5 text-gray-700 bg-gray-100 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            title="Refresh data"
          >
            <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </button>

          {/* Edit Mode Buttons */}
          {isEditing ? (
            <>
              {/* Cancel Button */}
              <button
                onClick={onCancel}
                disabled={saving}
                className="flex items-center gap-1.5 px-4 py-1.5 text-gray-700 bg-gray-100 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium"
              >
                <XMarkIcon className="h-4 w-4" />
                Cancel
              </button>

              {/* Save Button */}
              <button
                onClick={onSave}
                disabled={saving}
                className="flex items-center gap-1.5 px-4 py-1.5 text-white bg-blue-600 rounded hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed transition-colors text-sm font-medium"
              >
                {saving ? (
                  <ArrowPathIcon className="h-4 w-4 animate-spin" />
                ) : (
                  <CheckIcon className="h-4 w-4" />
                )}
                {saving ? 'Saving...' : hasUnsavedChanges ? 'Save' : 'Done'}
              </button>
            </>
          ) : (
            <>
              {/* Unlock Button for Executives (when outside editing window) */}
              {showUnlockButton && (
                <button
                  onClick={handleUnlock}
                  disabled={isDisabled}
                  className="flex items-center gap-1.5 px-4 py-1.5 text-amber-700 bg-amber-100 rounded hover:bg-amber-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium"
                  title="Grant 24-hour editing override"
                >
                  {unlocking ? (
                    <ArrowPathIcon className="h-4 w-4 animate-spin" />
                  ) : (
                    <LockOpenIcon className="h-4 w-4" />
                  )}
                  {unlocking ? 'Unlocking...' : 'Unlock (24hr)'}
                </button>
              )}

              {/* Edit Button - Show when can edit (within window OR has override OR archive unlocked) */}
              {canEdit && (
                <button
                  onClick={onStartEdit}
                  disabled={isDisabled}
                  className={`flex items-center gap-1.5 px-4 py-1.5 text-white rounded disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors text-sm font-medium ${
                    isArchiveUnlocked && !isCurrentTargetYear ? 'bg-amber-600 hover:bg-amber-700' : 'bg-blue-600 hover:bg-blue-700'
                  }`}
                >
                  <PencilIcon className="h-4 w-4" />
                  {isArchiveUnlocked && !isCurrentTargetYear ? 'Edit Archive' : 'Edit'}
                </button>
              )}

              {/* Show message for consolidated view */}
              {isConsolidated && (
                <span className="text-sm text-gray-500 italic">
                  Aggregated data from all locations (read-only)
                </span>
              )}

              {/* Show message if archived (not current year) and NOT unlocked */}
              {!isConsolidated && !isCurrentTargetYear && !isArchiveUnlocked && !isExecutive && (
                <span className="text-sm text-gray-500 italic">
                  Viewing archived projection (read-only)
                </span>
              )}

              {/* Show message if locked and not executive */}
              {isCurrentTargetYear && isFinal && !editingWindowStatus?.hasExecutiveOverride && !isExecutive && (
                <span className="text-sm text-gray-500 italic">
                  Editing window closed (Oct 1 - Dec 31)
                </span>
              )}
            </>
          )}
        </div>
      </div>
      </div>
    </>
  );
};
